import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Square, X } from 'lucide-react'
import type { CommentRegion } from '../types'

export interface SheetComment {
  id: string
  body: string
  createdAt: number
  /** Drawn markup region on the photo, if the reviewer boxed a spot. */
  region?: CommentRegion | null
  /** Threads a reply under the comment it answers; null/undefined = top level. */
  parentCommentId?: string | null
  /** Display names this comment @mentioned (already server-resolved against the
   *  link's reviewers). Drives the in-body highlight. */
  mentionNames?: string[]
  /** true while the optimistic local echo hasn't been confirmed by the server. */
  pending?: boolean
}

interface CommentSheetProps {
  open: boolean
  /** Comments already on this photo (public — the studio's internal notes never
   *  reach here, the worker filters them from the manifest). */
  comments: SheetComment[]
  onClose: () => void
  /** Send a new comment; resolves when accepted (or throws on failure). When
   *  parentCommentId is set the comment threads as a reply; region is the markup
   *  the reviewer drew (null = no region). */
  onSubmit: (
    body: string,
    parentCommentId: string | null,
    region: CommentRegion | null
  ) => Promise<void>
  /** The comment whose region is highlighted on the photo. */
  activeId?: string | null
  /** Select a comment → highlight its region on the photo (and vice versa). */
  onSelect?: (id: string | null) => void
  /** A region the reviewer has drawn but not yet sent — shown as a chip so they
   *  know the next comment will be anchored to it. */
  pendingRegion?: CommentRegion | null
  onClearPendingRegion?: () => void
}

/** Split a comment body on @mention tokens that match a known reviewer name so
 *  the matched run can be highlighted. Matching mirrors the worker: the @token
 *  is compared against each name's first word, case-insensitively. */
function highlightMentions(body: string, mentionNames: string[]) {
  if (mentionNames.length === 0) return body
  const firstNames = new Set(mentionNames.map((n) => n.trim().split(/\s+/)[0]?.toLowerCase()))
  const parts = body.split(/(@[a-z0-9_-]+)/gi)
  return parts.map((part, i) => {
    const m = /^@([a-z0-9_-]+)$/i.exec(part)
    if (m && firstNames.has(m[1].toLowerCase())) {
      return (
        <span key={i} data-testid="comment-mention" className="font-semibold text-[var(--accent)]">
          {part}
        </span>
      )
    }
    return <span key={i}>{part}</span>
  })
}

/**
 * A reviewer's comment thread for one photo — distinct from the private Note
 * (which is tied to a pick). Comments go back to the photographer's review
 * inbox. Replies nest under their parent; @mentions of known reviewers are
 * highlighted. A comment can also be anchored to a drawn region on the photo
 * (markup); those rows show a marker icon and select their box when tapped.
 * Mirrors NoteSheet's bottom-sheet styling.
 */
export function CommentSheet({
  open,
  comments,
  onClose,
  onSubmit,
  activeId,
  onSelect,
  pendingRegion,
  onClearPendingRegion,
}: CommentSheetProps) {
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** The comment being replied to (null = a new top-level comment). */
  const [replyTo, setReplyTo] = useState<SheetComment | null>(null)
  const taRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (open) {
      setText('')
      setError(null)
      setReplyTo(null)
    }
  }, [open])

  // Group comments into top-level + their replies, preserving chronological
  // order within each thread. An optimistic reply whose parent is present nests;
  // anything orphaned falls back to top-level so it's never dropped.
  const threads = useMemo(() => {
    const byParent = new Map<string, SheetComment[]>()
    const tops: SheetComment[] = []
    const ids = new Set(comments.map((c) => c.id))
    for (const c of comments) {
      if (c.parentCommentId && ids.has(c.parentCommentId)) {
        const arr = byParent.get(c.parentCommentId) ?? []
        arr.push(c)
        byParent.set(c.parentCommentId, arr)
      } else {
        tops.push(c)
      }
    }
    return tops.map((t) => ({ comment: t, replies: byParent.get(t.id) ?? [] }))
  }, [comments])

  const listRef = useRef<HTMLUListElement | null>(null)
  const [hasMoreBelow, setHasMoreBelow] = useState(false)
  /** True while there is comment text below the fold, so the fade can say so. */
  const updateOverflow = useCallback(() => {
    const el = listRef.current
    if (!el) return
    setHasMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 4)
  }, [])
  useEffect(updateOverflow, [updateOverflow, threads.length])

  const send = async () => {
    const body = text.trim()
    if (!body || sending) return
    setSending(true)
    setError(null)
    try {
      await onSubmit(body, replyTo?.id ?? null, pendingRegion ?? null)
      setText('')
      setReplyTo(null)
    } catch {
      setError('Could not send — check your connection and try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/55 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.div
            data-testid="comment-sheet"
            // Opens at a SHORTER detent. At calc(100dvh - 1rem) it could take
            // roughly 60% of a 390x844 phone, so a client writing "keep the
            // veil edge" could not see the veil while typing — the sheet
            // covered the frame it was about. 62dvh leaves the photo readable
            // and still fits the field, the button and a comment or two; the
            // drag handle expands it for a long thread.
            className="fixed bottom-0 left-0 right-0 z-50 mx-auto flex max-h-[62dvh] flex-col gap-3.5 overflow-y-auto rounded-t-3xl border border-b-0 border-[var(--border-default)] bg-[var(--surface-primary)] px-5 pb-7 pt-3 md:max-h-[calc(100dvh-1rem)] md:max-w-[440px]"
            style={{ boxShadow: '0 -20px 40px rgba(0,0,0,0.6)' }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 400, damping: 40 }}
            role="dialog"
            aria-modal="true"
            aria-label="Comments"
          >
            <div className="w-9 h-1 rounded-full bg-[var(--border-strong)] mx-auto" />
            <div className="text-[17px] font-semibold">Comments</div>

            {threads.length > 0 ? (
              /* The list has always scrolled, but nothing said so: a long note
                 ended mid-word against a hard edge, and macOS hides overlay
                 scrollbars until you move, so a client could not tell their
                 photographer's instruction continued. The fade is the signal;
                 it sits over the bottom edge and ignores pointer events so it
                 cannot block a tap on the last comment. */
              <div className="relative min-h-0">
              <ul
                ref={listRef}
                onScroll={updateOverflow}
                data-testid="comment-sheet-list"
                className="flex flex-col gap-2 max-h-48 overflow-y-auto"
              >
                {threads.map(({ comment, replies }) => (
                  <li key={comment.id} className="flex flex-col gap-1.5">
                    <CommentBubble
                      comment={comment}
                      active={comment.id === activeId}
                      onReply={() => setReplyTo(comment)}
                      onSelect={onSelect}
                    />
                    {replies.length > 0 && (
                      <ul
                        data-testid={`comment-replies-${comment.id}`}
                        className="flex flex-col gap-1.5 pl-4 border-l border-[var(--border-subtle)] ml-1"
                      >
                        {replies.map((r) => (
                          <li key={r.id}>
                            <CommentBubble
                              comment={r}
                              active={r.id === activeId}
                              onReply={() => setReplyTo(comment)}
                              onSelect={onSelect}
                            />
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
              {hasMoreBelow && (
                <div
                  data-testid="comment-sheet-more"
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-10 rounded-b bg-gradient-to-t from-[var(--surface-primary)] to-transparent"
                />
              )}
              </div>
            ) : (
              <div className="text-[13px] text-[var(--text-tertiary)]">
                No comments yet — start the conversation with your photographer.
              </div>
            )}

            {pendingRegion && (
              <div
                data-testid="comment-sheet-pending-region"
                className="flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-[12px]"
                style={{
                  background: 'var(--surface-secondary)',
                  border: '1px solid var(--accent-primary)',
                  color: 'var(--text-secondary)',
                }}
              >
                <span className="flex items-center gap-1.5">
                  <Square size={12} strokeWidth={2.5} style={{ color: 'var(--accent-primary)' }} />
                  Anchored to the spot you marked
                </span>
                <button
                  type="button"
                  data-testid="comment-sheet-clear-region"
                  aria-label="Remove marked region"
                  onClick={onClearPendingRegion}
                  className="grid place-items-center w-6 h-6 rounded-full text-[var(--text-tertiary)]"
                >
                  <X size={13} />
                </button>
              </div>
            )}

            {replyTo && (
              <div
                data-testid="comment-reply-banner"
                className="flex items-center justify-between text-[12px] text-[var(--text-secondary)] bg-[var(--surface-secondary)] rounded-xl px-3 py-2"
              >
                <span className="truncate">Replying to “{replyTo.body.slice(0, 40)}”</span>
                <button
                  type="button"
                  data-testid="comment-reply-cancel"
                  aria-label="Cancel reply"
                  onClick={() => setReplyTo(null)}
                  className="ml-2 shrink-0 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                >
                  Cancel
                </button>
              </div>
            )}

            <textarea
              ref={taRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={replyTo ? 'Write a reply…' : 'Leave a comment for your photographer…'}
              maxLength={2000}
              className="bg-[var(--surface-secondary)] border border-[var(--border-default)] rounded-2xl p-3.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] resize-none focus:outline-none focus:border-[var(--border-strong)] min-h-20"
              autoFocus
            />
            {error && (
              <div className="text-[12px] text-[var(--accent-danger)]" role="alert">
                {error}
              </div>
            )}
            <button
              data-testid="comment-sheet-send"
              onClick={send}
              disabled={sending || text.trim().length === 0}
              className="h-12 rounded-2xl bg-[var(--accent-primary)] text-[#0a0a0a] font-semibold text-sm disabled:opacity-50"
            >
              {sending ? 'Sending…' : replyTo ? 'Send reply' : 'Send comment'}
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

function CommentBubble({
  comment,
  active,
  onReply,
  onSelect,
}: {
  comment: SheetComment
  active?: boolean
  onReply: () => void
  onSelect?: (id: string | null) => void
}) {
  const hasRegion = Boolean(comment.region)
  return (
    <div
      data-testid={`comment-${comment.id}`}
      className="bg-[var(--surface-secondary)] rounded-2xl px-3.5 py-2.5 text-sm text-[var(--text-primary)]"
      style={{
        opacity: comment.pending ? 0.6 : 1,
        border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
      }}
    >
      {hasRegion ? (
        // A region-anchored comment is a button so tapping it highlights its box.
        <button
          type="button"
          data-testid={`comment-sheet-item-${comment.id}`}
          aria-pressed={active}
          onClick={() => onSelect?.(active ? null : comment.id)}
          className="w-full text-left flex items-start gap-2"
        >
          <Square
            size={13}
            strokeWidth={2.5}
            className="mt-0.5 shrink-0"
            style={{ color: 'var(--accent-primary)' }}
            aria-label="Marked on photo"
          />
          <span>{highlightMentions(comment.body, comment.mentionNames ?? [])}</span>
        </button>
      ) : (
        <div>{highlightMentions(comment.body, comment.mentionNames ?? [])}</div>
      )}
      {!comment.pending && (
        <button
          type="button"
          data-testid={`comment-reply-${comment.id}`}
          onClick={onReply}
          className="mt-1 text-[11px] font-semibold text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
        >
          Reply
        </button>
      )}
    </div>
  )
}
